const STYTCH_COOKIE_NAMES = ['stytch_session', 'stytch_session_jwt'] as const;
const STYTCH_COOKIE_PATTERN = new RegExp(`^\\s*(?:${STYTCH_COOKIE_NAMES.join('|')})=([^;]*)`);

declare global {
  interface Document {
    __stytchSessionCookieOverrideInstalled?: boolean;
  }
}

export function rewriteStytchCookieAsSessionCookie(cookieString: string): string {
  if (typeof cookieString !== 'string') return cookieString;

  const match = cookieString.match(STYTCH_COOKIE_PATTERN);
  if (!match) return cookieString;

  // Deletion writes carry an empty value (revoke/logout). Pass through untouched
  // so the cookie actually clears instead of being orphaned with no value.
  if (match[1].trim() === '') return cookieString;

  return cookieString.replace(/;\s*max-age=[^;]+/gi, '').replace(/;\s*expires=[^;]+/gi, '');
}

/**
 * Overrides document.cookie writes so the Stytch session cookies are stored as
 * browser-session cookies (no Max-Age / Expires) and are cleared when the
 * browser is closed. Server-side session lifetime is unchanged.
 *
 * The Stytch SDK writes its cookies via document.cookie with Max-Age set to
 * session_duration_minutes and re-writes them every ~3 minutes during JWT
 * refresh. Intercepting the setter is the only way to keep every write —
 * initial auth and each refresh — as a browser-session cookie.
 */
export function installStytchSessionCookieOverride(): void {
  if (typeof document === 'undefined') return;
  if (document.__stytchSessionCookieOverrideInstalled) return;

  const descriptor = Object.getOwnPropertyDescriptor(Document.prototype, 'cookie');
  if (!descriptor?.set || !descriptor.get) return;

  const originalSet = descriptor.set;
  const originalGet = descriptor.get;

  Object.defineProperty(document, 'cookie', {
    configurable: true,
    get() {
      return originalGet.call(this);
    },
    set(value: string) {
      originalSet.call(this, rewriteStytchCookieAsSessionCookie(value));
    },
  });

  document.__stytchSessionCookieOverrideInstalled = true;
}
